import { getTranslatedProperty, getProductRoute } from '@shopware-pwa/helpers-next';
import type { Schemas } from '@shopware/api-client/api-types';
import type { Script } from 'unhead/types';
import type { MetaEntry } from '../../types/seo';

export function useProductSeo(product: Ref<Schemas['Product']>, navigationId: string) {
    const { apiClient } = useShopwareContext();
    const unrefProduct = unref<Schemas['Product']>(product);
    const { t } = useI18n();
    const { currency } = useSessionContext();
    const { meta: cmsMeta } = useCmsMeta(unrefProduct);
    const url = useRequestURL();

    const { sessionContext } = useSessionContext();
    const salesChannelId = computed(() => sessionContext.value?.salesChannel?.id);

    const title = computed(() => {
        const titleMeta = cmsMeta.value.find((meta) => meta.name === 'title');

        if (titleMeta) {
            return t('layout.seo.productTitle', { title: titleMeta.content });
        }

        const productName = getTranslatedProperty(unrefProduct, 'name');

        return t('layout.seo.productTitle', { title: productName });
    });

    const meta: ComputedRef<MetaEntry[]> = computed(() => {
        const cmsMetaValue = cmsMeta.value
            .filter((meta) => meta.name !== 'title');
        const descriptionMeta = cmsMeta.value.find((meta) => meta.name === 'description');

        if (!descriptionMeta) {
            const productName = getTranslatedProperty(unrefProduct, 'name');
            const description = getTranslatedProperty(unrefProduct, 'description');

            cmsMetaValue.push({
                name: 'description',
                content: t('layout.seo.productMetaDescription', { title: productName, description })?.slice(0, 170),
            });
        }

        cmsMetaValue.push({
            name: 'title',
            content: title.value,
        });

        cmsMetaValue.push({
            name: 'url',
            content: url.href,
        });

        if (unrefProduct.cover?.media?.url) {
            cmsMetaValue.push({
                name: 'image',
                content: unrefProduct.cover?.media?.url,
            });
        }

        return cmsMetaValue;
    });

    const scripts: ComputedRef<Script[]> = computed(() => {
        const unitPrice = product.value.calculatedPrices?.[0]?.unitPrice;
        if (!unitPrice) {
            return [];
        }
        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: getTranslatedProperty(product.value, 'name'),
            url: url.href,
            image: product.value.media?.map((media) => media.media.url),
            sku: product.value.productNumber,
            description: getTranslatedProperty(product.value, 'description'),
        };

            schema.offers = {
                '@type': 'Offer',
                url: url.href,
                ...(unitPrice && unitPrice > 0 ? {
                    priceCurrency: currency.value?.isoCode,
                    price: unitPrice,
                } : {}),
                availability: product.value.available ? 'https://schema.org/InStock' : 'http://schema.org/PreOrder',
                itemCondition: 'https://schema.org/NewCondition',
                eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
                businessFunction: 'http://purl.org/goodrelations/v1#Sell',
                seller: {
                    '@type': 'Organization',
                    name: product.value.extensions.vendors[0]?.name,
                },
            };

        // conditionally add 'mpn' if it exists
        if (product.value.ean) {
            schema.mpn = product.value.ean;
        }

        // conditionally add 'brand' if the manufacturer and its name exist
        const brandName = getTranslatedProperty(product.value.manufacturer, 'name');
        if (brandName) {
            schema.brand = {
                '@type': 'Brand',
                name: brandName,
            };
        }

        const productScript = {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(schema),
            key: 'typeProduct',
        };

        return [ productScript ];
    });

    const canonicalUrl = computed(() => {
        if (product.value.canonicalProductId) {
            return url.origin + getProductRoute(product.value.canonicalProduct).path;
        }

        return (product.value.customFields as Record<string, string | undefined>)?.['seo_xtreme_canonical_url'];
    });

    useMeta({
        title,
        meta,
        canonicalUrl,
        salesChannelId: salesChannelId.value,
    });

    useStructuredData({
        scripts,
        salesChannelId: salesChannelId.value,
    });

    let productId = unrefProduct.id;
    if (navigationId !== unrefProduct.id && unrefProduct.parentId && unrefProduct.parentId === navigationId) {
        productId = navigationId;
    }

    const { data: hreflangData } = useLazyAsyncData(`seo-hreflang-product-${productId}`, async () => {
        const { data } = await apiClient.invoke(`productHreflangInformation get /seo/hreflang-information/product/${productId}`);

        return data.hreflangSalesChannelInformation;
    });

    const { hreflangLinks } = useHreflangLinks(hreflangData, false, true);

    useHead(() => ({
        link: [
            ...hreflangLinks.value,
            {
                rel: 'alternate',
                hreflang: 'x-default',
                href: canonicalUrl.value,
            },
        ],
    }));
}
